import React, { useState, useEffect, useCallback } from "react";
import {
  X,
  FileText,
  Calendar,
  CheckCircle,
  Clock,
  AlertCircle,
} from "lucide-react";
import { formatDate } from "../utils/dateUtils";
import { Invoice } from "../types";

interface InvoiceListModalProps {
  isOpen: boolean;
  onClose: () => void;
  clientId: string;
  clientName: string;
}

export const InvoiceListModal: React.FC<InvoiceListModalProps> = ({
  isOpen,
  onClose,
  clientId,
  clientName,
}) => {
  const [invoices, setInvoices] = useState<Invoice[]>([]);
  const [loading, setLoading] = useState(false);

  const apiUrl = process.env.REACT_APP_API_URL || "http://localhost:5000";

  const fetchInvoices = useCallback(async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem("token");
      // Backend now handles Contact ID -> Client ID mapping automatically
      const response = await fetch(
        `${apiUrl}/api/invoices?clientId=${clientId}`,
        {
          headers: { Authorization: `Bearer ${token}` },
        },
      );

      const data = await response.json();
      if (data.success) {
        setInvoices(data.data);
      }
    } catch (error) {
      console.error("Failed to fetch invoices:", error);
    } finally {
      setLoading(false);
    }
  }, [clientId, apiUrl]);

  useEffect(() => {
    if (isOpen && clientId) {
      fetchInvoices();
    }
  }, [isOpen, clientId, fetchInvoices]);

  const formatCurrency = (amount: number, currencyCode?: string) => {
    return new Intl.NumberFormat("en-US", {
      style: "currency",
      currency: currencyCode || "USD",
    }).format(amount);
  };

  const formatDateLocal = (date?: Date | string) => {
    if (!date) return "N/A";
    return formatDate(date);
  };

  const getStatusConfig = (status: string, amountDue?: number) => {
    if (status === "PAID" || (amountDue !== undefined && amountDue === 0)) {
      return {
        icon: CheckCircle,
        color: "text-success-600 dark:text-success-400",
        bg: "bg-green-50 dark:bg-gray-700 dark:border dark:border-success-800/50",
        border: "border-green-200 dark:border-green-800",
        label: "Paid",
      };
    }
    if (status === "APPROVED" || status === "SUBMITTED") {
      return {
        icon: Clock,
        color: "text-primary-600 dark:text-primary-400",
        bg: "bg-primary-50 dark:bg-gray-700 dark:border dark:border-primary-800/50",
        border: "border-primary-200 dark:border-primary-800",
        label: "Unpaid",
      };
    }
    if (status === "VOIDED" || status === "DELETED") {
      return {
        icon: X,
        color: "text-error-600 dark:text-error-400",
        bg: "bg-red-50 dark:bg-gray-700 dark:border dark:border-error-800/50",
        border: "border-red-200 dark:border-red-800",
        label: status,
      };
    }
    return {
      icon: AlertCircle,
      color: "text-gray-600 dark:text-gray-400",
      bg: "bg-gray-50 dark:bg-gray-800/60",
      border: "border-gray-200 dark:border-gray-700",
      label: status,
    };
  };

  if (!isOpen) return null;

  const paidInvoices = invoices.filter(
    (inv) =>
      inv.status === "PAID" ||
      (inv.amountDue !== undefined && inv.amountDue === 0),
  );
  const unpaidInvoices = invoices.filter(
    (inv) =>
      inv.status !== "PAID" &&
      inv.status !== "VOIDED" &&
      inv.status !== "DELETED" &&
      (inv.amountDue === undefined || inv.amountDue > 0),
  );

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl w-full max-w-4xl max-h-[90vh] overflow-hidden flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-gray-200 dark:border-gray-600">
          <div className="flex items-center space-x-3">
            <div className="w-10 h-10 bg-accent-100 dark:bg-gray-700 dark:border dark:border-accent-800/50 rounded-lg flex items-center justify-center">
              <FileText className="w-5 h-5 text-accent-600 dark:text-accent-400" />
            </div>
            <div>
              <h2 className="text-xl font-semibold text-gray-900 dark:text-gray-100">
                Invoices
              </h2>
              <p className="text-sm text-gray-600 dark:text-gray-400">
                {clientName}
              </p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 transition-colors"
          >
            <X className="w-6 h-6" />
          </button>
        </div>

        {/* Summary Stats */}
        <div className="grid grid-cols-3 gap-4 p-6 border-b border-gray-200 dark:border-gray-600">
          <div className="text-center">
            <div className="text-2xl font-bold text-success-600 dark:text-success-400">
              {paidInvoices.length}
            </div>
            <div className="text-sm text-gray-600 dark:text-gray-400">Paid</div>
          </div>
          <div className="text-center">
            <div className="text-2xl font-bold text-primary-600 dark:text-primary-400">
              {unpaidInvoices.length}
            </div>
            <div className="text-sm text-gray-600 dark:text-gray-400">
              Unpaid
            </div>
          </div>
          <div className="text-center">
            <div className="text-2xl font-bold text-gray-600 dark:text-gray-400">
              {invoices.length}
            </div>
            <div className="text-sm text-gray-600 dark:text-gray-400">
              Total
            </div>
          </div>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-6">
          {loading ? (
            <div className="text-center py-8">
              <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-accent-600"></div>
              <p className="text-gray-600 dark:text-gray-400 mt-2">
                Loading invoices...
              </p>
            </div>
          ) : invoices.length === 0 ? (
            <div className="text-center py-8 text-gray-500 dark:text-gray-400">
              <FileText className="w-12 h-12 mx-auto mb-3 opacity-50" />
              <p>No invoices found</p>
            </div>
          ) : (
            <div className="space-y-3">
              {invoices.map((invoice) => {
                const config = getStatusConfig(
                  invoice.status,
                  invoice.amountDue,
                );
                const StatusIcon = config.icon;

                return (
                  <div
                    key={invoice._id}
                    className={`border rounded-lg p-4 ${config.border} ${config.bg} hover:shadow-md transition-shadow`}
                  >
                    <div className="flex items-start justify-between">
                      <div className="flex items-start space-x-3 flex-1">
                        <StatusIcon
                          className={`w-5 h-5 ${config.color} mt-0.5 flex-shrink-0`}
                        />
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center space-x-2 mb-1">
                            <h3 className="text-sm font-semibold text-gray-900 dark:text-gray-100">
                              {invoice.invoiceNumber}
                            </h3>
                            <span
                              className={`text-xs font-medium px-2 py-0.5 rounded ${config.color}`}
                            >
                              {config.label}
                            </span>
                          </div>

                          {invoice.reference && (
                            <p className="text-xs text-gray-600 dark:text-gray-400 mb-2">
                              Ref: {invoice.reference}
                            </p>
                          )}

                          <div className="grid grid-cols-2 gap-2 text-xs">
                            <div className="flex items-center space-x-1 text-gray-600 dark:text-gray-400">
                              <Calendar className="w-3 h-3" />
                              <span>Date: {formatDateLocal(invoice.date)}</span>
                            </div>
                            <div className="flex items-center space-x-1 text-gray-600 dark:text-gray-400">
                              <Calendar className="w-3 h-3" />
                              <span>
                                Due: {formatDateLocal(invoice.dueDate)}
                              </span>
                            </div>
                          </div>
                        </div>
                      </div>

                      <div className="text-right ml-4">
                        <div className="text-lg font-bold text-gray-900 dark:text-gray-100">
                          {formatCurrency(invoice.total, invoice.currencyCode)}
                        </div>
                        {invoice.amountDue !== undefined &&
                          invoice.amountDue > 0 && (
                            <div className="text-xs text-primary-600 dark:text-primary-400 font-medium">
                              Due:{" "}
                              {formatCurrency(
                                invoice.amountDue,
                                invoice.currencyCode,
                              )}
                            </div>
                          )}
                        {invoice.amountPaid !== undefined &&
                          invoice.amountPaid > 0 && (
                            <div className="text-xs text-success-600 dark:text-success-400">
                              Paid:{" "}
                              {formatCurrency(
                                invoice.amountPaid,
                                invoice.currencyCode,
                              )}
                            </div>
                          )}
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="p-6 border-t border-gray-200 dark:border-gray-600">
          <button
            onClick={onClose}
            className="w-full px-4 py-2 bg-gray-200 dark:bg-gray-700 text-gray-900 dark:text-gray-100 rounded-lg hover:bg-gray-300 dark:hover:bg-gray-600 transition-colors"
          >
            Close
          </button>
        </div>
      </div>
    </div>
  );
};
